import type { Marketplace } from "@wbly/common";
import type { FC } from "react";
import type { NavItemType, PillsSlice } from "@wbly/data-storefront";
import { getLocaleUrl, removeLocalePrefix } from "@wbly/common";
import { trackPillClick } from "../track-pills-events";
import { getPillsTaxon, buildCategoryPathname } from "./utils/pills";
import type { PillItem } from "./utils/pills";

import styles from "./Pills.module.css";

type PillsProps = {
  pathname: string;
  marketplace: Marketplace;
  taxonCategoryName?: string;
  items?: PillsSlice["body"]["items"];
  navItems?: NavItemType[];
  id: string;
};

type PillsListProps = {
  items: PillItem[];
  id: string;
};

const { container, item } = styles;

export const PillsList: FC<PillsListProps> = ({ items, id }) => {
  if (!items.length) return null;

  return (
    <ul className={container} id={id} data-testid={id}>
      {items.map((taxon) => {
        // Don't render the pill if there's no href
        if (!taxon.url && !taxon.link) return;

        const href = taxon.link ? taxon.link : taxon.url;

        return (
          <li className={item} key={taxon.label}>
            <a
              href={href}
              onClick={() => {
                trackPillClick(href);
              }}
            >
              {taxon.label}
            </a>
          </li>
        );
      })}
    </ul>
  );
};

export const PillsComponent: FC<PillsProps> = ({
  pathname,
  marketplace,
  taxonCategoryName,
  items,
  navItems,
  id,
}) => {
  let pillItems: PillItem[] | null = null;
  // Return curated items if they exist.
  if (items?.length) return <PillsList items={items} id={id} />;

  // Return null if any of the required props are missing and items are empty
  if (!pathname && !taxonCategoryName) return null;

  // Return second level nav items if no taxon category is selected.
  if (!taxonCategoryName) {
    const pillPathname = buildCategoryPathname(null, pathname, marketplace);
    const localisedPathname = getLocaleUrl(
      marketplace.identifier,
      removeLocalePrefix(marketplace.identifier, pillPathname)
    );

    pillItems = getPillsTaxon(localisedPathname, navItems, marketplace);

    return pillItems?.length ? <PillsList id={id} items={pillItems} /> : null;
  }

  // Return third level nav items for the selected taxon category.
  // If no items are found, return null.
  const pillPathname = buildCategoryPathname(
    taxonCategoryName,
    // We use Astro pathname here because it includes the locale prefix
    // for 'us' as well. This is wanted in order to manipulate the url
    // and not having to care about exceptions in length for default or non default
    // locales.
    pathname,
    marketplace
  );

  const localisedPathname = getLocaleUrl(
    marketplace.identifier,
    removeLocalePrefix(marketplace.identifier, pillPathname)
  );

  pillItems = getPillsTaxon(
    // Once we did our manipulation, we'll remove the prefix
    // and we re-localize the url. This will remove the "us"
    // prefix from the url if is present.
    localisedPathname,
    navItems,
    marketplace
  );

  return pillItems?.length ? <PillsList id={id} items={pillItems} /> : null;
};
